import { useCallback, useMemo, useState } from 'react';

type Cursor = string | Record<string, unknown> | null;

type UseCursorPaginationOptions<TCursor extends Cursor = Cursor> = {
  initialPage?: number;
  onCursorReceived?: (cursor: TCursor) => void;
};

export function useCursorPagination<TCursor extends Cursor = Cursor>({
  initialPage = 1,
  onCursorReceived,
}: UseCursorPaginationOptions<TCursor> = {}) {
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [cursorHistory, setCursorHistory] = useState<TCursor[]>([null as TCursor]);

  const exclusiveStartKey: TCursor = cursorHistory[currentPage - 1] ?? (null as TCursor);

  const resetPagination = useCallback(() => {
    setCurrentPage(1);
    setCursorHistory([null as TCursor]);
  }, []);

  const handlePageChange = useCallback(
    (newPage: number, options?: { hasMore: boolean; responseKey: TCursor }) => {
      if (newPage === currentPage) return;

      if (newPage > currentPage) {
        if (options && (!options.hasMore || !options.responseKey)) return;

        if (options?.responseKey) {
          setCursorHistory((prev) => {
            const next = [...prev, options.responseKey as TCursor];
            return next;
          });
        }
        setCurrentPage(newPage);
        return;
      }

      setCursorHistory((prev) => prev.slice(0, Math.max(1, newPage)) as TCursor[]);
      setCurrentPage(Math.max(1, newPage));
    },
    [currentPage],
  );

  const updateCursor = useCallback(
    (cursor: TCursor) => {
      if (onCursorReceived) {
        onCursorReceived(cursor);
      }

      setCursorHistory((prev) => {
        if (prev.length === currentPage && cursor) {
          const next = [...prev, cursor];
          return next;
        }
        return prev;
      });
    },
    [currentPage, onCursorReceived],
  );

  const hasPrevious = useMemo(() => currentPage > 1, [currentPage]);

  return {
    currentPage,
    exclusiveStartKey,
    cursorHistory,
    hasPrevious,
    resetPagination,
    handlePageChange,
    updateCursor,
  };
}
